import type { Aircraft } from "../aircraft/Aircraft";
import type {
  AirportMap,
  MutableVec2,
  RunwayDefinition,
  WorldBounds,
} from "../airport/AirportDefinition";
import type { GameplayConfig } from "../config/gameplay";
import { clonePoint, distance, pathLength } from "../simulation/geometry";
import {
  departureExitPoint,
  hasDepartureConflict,
} from "../simulation/RunwaySystem";

/** Everything the scheduler needs to read from the session, per call. */
export interface DepartureContext {
  readonly map: AirportMap;
  readonly gameplay: GameplayConfig;
  readonly bounds: WorldBounds;
  readonly aircraft: readonly Aircraft[];
  readonly elapsed: number;
}

/**
 * Gets landed aircraft back into the air.
 *
 * Owns the queue order and the per-runway take-off clock; everything else it
 * touches lives on the aircraft record. Scoring, traffic counters and events
 * stay with `GameSession` — this only decides who rolls, and when.
 */
export class DepartureScheduler {
  private nextQueueOrder = 1;
  private lastTakeoffAt = new Map<string, number>();

  reset(): void {
    this.nextQueueOrder = 1;
    this.lastTakeoffAt = new Map();
  }

  /** Aircraft waiting on a runway, in the order they joined the queue. */
  queueFor(runwayId: string, aircraft: readonly Aircraft[]): Aircraft[] {
    return aircraft
      .filter(
        (item) =>
          item.target?.id === runwayId &&
          (item.state === "taxiing-out" || item.state === "holding"),
      )
      .sort(
        (a, b) =>
          (a.departureQueueOrder ?? Number.POSITIVE_INFINITY) -
          (b.departureQueueOrder ?? Number.POSITIVE_INFINITY),
      );
  }

  /** Seconds until the next take-off on a runway, or null when it is idle. */
  nextDepartureIn(runwayId: string, context: DepartureContext): number | null {
    const next =
      this.queueFor(runwayId, context.aircraft)[0]?.scheduledDepartureAt ??
      null;
    return next === null ? null : Math.max(0, next - context.elapsed);
  }

  /** Send a just-landed aircraft down the taxiway and give it a slot. */
  queue(aircraft: Aircraft, context: DepartureContext): void {
    const runway = aircraft.target;
    if (!runway) {
      return;
    }

    const route = context.map.groundRoutes[runway.id]?.departure;
    const path: MutableVec2[] =
      route && route.length > 0
        ? route.map(clonePoint)
        : [clonePoint(runway.approach)];
    if (distance(path[path.length - 1]!, runway.approach) > 0.01) {
      path.push(clonePoint(runway.approach));
    }

    aircraft.state = "taxiing-out";
    aircraft.path = path;
    aircraft.departureQueueOrder = this.nextQueueOrder++;

    const taxiSeconds =
      pathLength(aircraft.pos, path) / context.gameplay.taxiOutSpeed;
    aircraft.departureReadyAt =
      context.elapsed +
      Math.max(context.gameplay.departureTurnaroundSeconds, taxiSeconds);
    this.reschedule(runway.id, context);
  }

  isDue(aircraft: Aircraft, elapsed: number): boolean {
    return (
      aircraft.scheduledDepartureAt !== null &&
      elapsed >= aircraft.scheduledDepartureAt
    );
  }

  /**
   * Roll if the runway is clear, otherwise push the slot back.
   *
   * @returns the runway it launched from, or null when it had to wait.
   */
  tryLaunch(
    aircraft: Aircraft,
    context: DepartureContext,
  ): RunwayDefinition | null {
    const runway = aircraft.target;
    if (!runway) {
      return null;
    }

    if (this.conflicts(aircraft, context)) {
      aircraft.departureReadyAt =
        context.elapsed + context.gameplay.departureRetrySeconds;
      this.reschedule(runway.id, context);
      return null;
    }

    aircraft.state = "departing";
    aircraft.pos = clonePoint(runway.approach);
    aircraft.heading = Math.atan2(
      runway.end.y - runway.approach.y,
      runway.end.x - runway.approach.x,
    );
    aircraft.path = [
      clonePoint(runway.end),
      departureExitPoint(runway, context.bounds),
    ];
    aircraft.takeoffProgress = 0;
    aircraft.departureReadyAt = null;
    aircraft.scheduledDepartureAt = null;
    aircraft.departureQueueOrder = null;

    this.lastTakeoffAt.set(runway.id, context.elapsed);
    this.reschedule(runway.id, context);
    return runway;
  }

  /** Restack a runway's queue behind its last take-off. */
  private reschedule(runwayId: string, context: DepartureContext): void {
    let last = this.lastTakeoffAt.get(runwayId) ?? Number.NEGATIVE_INFINITY;
    for (const aircraft of this.queueFor(runwayId, context.aircraft)) {
      const ready = aircraft.departureReadyAt ?? context.elapsed;
      aircraft.scheduledDepartureAt = Math.max(
        ready,
        last + context.gameplay.departureDispatchSeconds,
        context.elapsed,
      );
      last = aircraft.scheduledDepartureAt;
    }
  }

  private conflicts(aircraft: Aircraft, context: DepartureContext): boolean {
    if (!aircraft.target) {
      return false;
    }
    const options = {
      departureSpeed: context.gameplay.departureSpeed,
      departureAirborneProgress: context.gameplay.departureAirborneProgress,
      conflictFactor: context.gameplay.departureConflictFactor,
    };
    return context.aircraft.some(
      (other) =>
        other !== aircraft &&
        other.state === "departing" &&
        other.target !== null &&
        hasDepartureConflict(aircraft, other, context.bounds, options),
    );
  }
}
